// Typ, nie moduł: `import type` znika przy transpilacji, więc test w środowisku
// node nie ciągnie za sobą `@tanstack/react-charts` ani Reacta.
import type { LineChartSeries } from '@/components/charts/LineChart.tsx';
import { CHART_PALETTE } from '@/components/ui/chartPalette.ts';
import { TimeSeriesDto } from '@/types/stats/TimeSeriesDto.ts';
import { reportStatsContractBreach } from '@/types/stats/statsContract.ts';
import { formatDayMonth } from '@/utils/dateFormatters.ts';

/** Wygląd jednej linii: napis legendy plus to, czym różni się kreska. */
export interface TimeSeriesLineStyle {
  label: string;
  color: string;
  /** Linia przerywana — wartość niepewna: prognoza, benchmark. */
  dashed?: boolean;
  filled?: boolean;
  emphasized?: boolean;
}

export type TimeSeriesRegistry = Record<string, TimeSeriesLineStyle>;

const lineStyle = (
  key: string,
  registry: TimeSeriesRegistry,
): TimeSeriesLineStyle => {
  const style = registry[key];
  if (style) return style;

  reportStatsContractBreach(
    `Nieznana seria czasowa: "${key}". Dopisz ją do rejestru widgetu.`,
  );
  return { label: key, color: CHART_PALETTE.muted };
};

/**
 * Serie z kontraktu na serie wykresu. Linia bez punktów wypada z wyniku —
 * pusta prognoza nie ma zostawiać martwego wiersza w legendzie.
 */
export const timeSeriesToLineSeries = (
  series: TimeSeriesDto,
  registry: TimeSeriesRegistry,
): LineChartSeries[] =>
  series.series
    .filter((line) => line.points.length > 0)
    .map((line) => {
      const style = lineStyle(line.key, registry);

      return {
        id: line.key,
        label: style.label,
        color: style.color,
        dashed: style.dashed,
        filled: style.filled,
        emphasized: style.emphasized,
        points: line.points.map((point) => ({
          label: formatDayMonth(point.periodStart),
          value: point.value,
        })),
      };
    });

/** Ostatnia wartość linii o danym kluczu — `null`, gdy linii nie ma albo jest pusta. */
export const seriesEndValue = (
  series: TimeSeriesDto,
  key: string,
): number | null => {
  const line = series.series.find((candidate) => candidate.key === key);
  if (!line || line.points.length === 0) return null;

  return line.points[line.points.length - 1].value;
};
